import * as _ from 'lodash';
import { AreasWithStackingGroup, Dimensions, Group, LoadsCube } from 'src/app/core/types';
import { Vector3 } from 'three';
import { Area, AreaType } from './area';
import { Load } from './load';
import { LoadingSpace } from './loading-space';
import { Outside } from './outside';
import { calculateRotatedObjectData } from './utils/boundingBoxData';
import { calculateYOffset, isItemOnSlope, ItemOnSlope } from './utils/loadDragAirContainer';
import { createPCTFreeSpaces, mergeAreas } from './utils/loadDragPCT';

export class AreasCalculator {
  public recalculateAreasAfterLoadPickUp(
    load: Load,
    loadingSpace: LoadingSpace,
    outside: Outside,
    areas: Area[],
    groups: Group[],
    areasWithStackingGroups: AreasWithStackingGroup[],
    loads: Load[],
  ): Area[] {
    const isAirContainer: boolean = loadingSpace.type === 4;
    if (isAirContainer) {
      areasWithStackingGroups = [];
      const isItemOnSlopes: ItemOnSlope = isItemOnSlope(load, loadingSpace);
      const isLoadOnSlope: boolean =
        isItemOnSlopes.isItemOnLeftSlope || isItemOnSlopes.isItemOnRightSlope;
      if (isLoadOnSlope) {
        const offset: number = Math.round(
          calculateYOffset(load, isItemOnSlopes.isItemOnLeftSlope, loadingSpace),
        );
        load.y = load.y > offset ? load.y : 0;
      }
    }
    const barrelAreas: Area[] = areas.filter((a) => a.type === 5 || a.type === 1);
    areas = areas.filter((a: Area) => a.type !== 5 && a.type !== 1);
    const isRotated: number = load.yRotation || load.edgeRotation;
    const groupsWithThisLoad: Group[] = groups.filter(
      (g: Group) => g.stackingOptions && g.id === load.idGroup,
    );
    const areasWithThisGroups: AreasWithStackingGroup[] = areasWithStackingGroups.filter(
      (g: AreasWithStackingGroup): boolean => g.id === load.idGroup,
    );
    let areasThisGroup: Area[] = [];
    areasWithThisGroups.forEach(
      (g: AreasWithStackingGroup) => (areasThisGroup = areasThisGroup.concat(g.areas)),
    );
    if (load.stacking && areasWithThisGroups) {
      let maxLoadsOnTop: number | string;
      const loadsCubeGroup: { loadsPerAxisY: number; load: Area }[] = [];
      groupsWithThisLoad.forEach((g: Group): void => {
        maxLoadsOnTop = g.stackingOptions.maxLoadsOnTop;
        g.loadsCubes.forEach((l: LoadsCube): void => {
          if (l.position) {
            const dimensions: Dimensions = l.dimensions ? l.dimensions : g.dimensions;
            const areaTmp: Area = new Area(
              dimensions.length * l.loadsPerAxis.z,
              dimensions.width * l.loadsPerAxis.x,
              dimensions.height * l.loadsPerAxis.y,
              l.position.x,
              l.position.y,
              l.position.z,
            );
            areaTmp.times100();
            loadsCubeGroup.push({ loadsPerAxisY: l.loadsPerAxis.y, load: areaTmp });
          }
        });
      });
      let loadsToDeleteAreaTop: Area[] = [];
      loadsCubeGroup.forEach((gl: { load: Area }): void => {
        const samePosition = loadsCubeGroup.filter((gl1: { load: Area }) =>
          gl1.load.isSamePositionAround(gl.load),
        );
        const countAreas = {};
        samePosition.forEach((gl1) => {
          for (let i = 0; i < gl1.loadsPerAxisY; i++) {
            const height = gl1.load.height / gl1.loadsPerAxisY;
            countAreas[height * i + gl1.load.y] =
              ++(countAreas[height * i + gl1.load.y] as number) || 1;
          }
        });
        if (_.size(countAreas) >= parseInt(`${maxLoadsOnTop}`, 10)) {
          let max = 0;
          let loadToDeleteAreaTop: Area;
          for (let i = 0; i < samePosition.length; i++) {
            if (samePosition[i].load.y >= max) {
              max = samePosition[i].load.y;
              loadToDeleteAreaTop = samePosition[i].load;
            }
          }
          loadsToDeleteAreaTop.push(loadToDeleteAreaTop);
        }
      });
      loadsToDeleteAreaTop = this.removeSameArea(loadsToDeleteAreaTop);
      loadsToDeleteAreaTop.forEach((l: Area): void => {
        areasThisGroup = areasThisGroup.filter((a: Area) => !a.isSamePosition(l));
      });
      areas = areas.concat(areasThisGroup);
      areasThisGroup.forEach((area: Area): void => {
        const areasYPosition: Area[] = areas.filter((a: Area): boolean => a.y === area.y);
        areas = areas.concat(this.extendAreasAroundArea(area, areasYPosition));
      });
      areas = this.removeContainedAreas(areas);
    }

    let areasOnLoadHeightLevel: Area[] = [];
    const areasOnLoadYLevel: Area[] = areas.filter((area: Area): boolean => area.y === load.y);
    const spaceHeight: number = this.getHeightOfSpaceThatHasLoadPosition(
      new Vector3(load.x, load.y, load.z),
      loadingSpace,
      outside,
    );
    let otherAreas: Area[];

    if (load.stacking) {
      otherAreas = areas.filter(
        (area: Area) => !(area.y === load.y || area.y === load.y + load.height),
      );
      const topLoadArea: Area = new Area(
        load.length,
        load.width,
        spaceHeight - load.y,
        load.x,
        load.y + load.height,
        load.z,
        AreaType.default,
      );
      const areasOnTopLoadLevel: Area[] = areas.filter(
        (area: Area): boolean => area.y === load.y + load.height,
      );
      areasOnLoadHeightLevel = this.splitAreasAroundArea(topLoadArea, areasOnTopLoadLevel);
    } else {
      otherAreas = areas.filter((area: Area) => !(area.y === load.y));
    }
    let loadLength = load.length;
    let loadWidth = load.width;
    const loadY = load.y;
    let loadX = load.x;
    let loadZ = load.z;
    if (isRotated) {
      const areaPoints: Partial<Load> = calculateRotatedObjectData(load);
      loadX = _.round(areaPoints.position.x, 2);
      loadZ = _.round(areaPoints.position.z, 2);
      loadWidth = _.round(areaPoints.width, 2);
      loadLength = _.round(areaPoints.length, 2);
    }

    const loadArea: Area = new Area(
      loadLength,
      loadWidth,
      spaceHeight - loadY,
      loadX,
      loadY,
      loadZ,
      AreaType.loadStamp,
    );
    let newAreas: Area[] = [loadArea];
    const tempAreas: Area[] = this.filterAreasAroundArea(loadArea, areasOnLoadYLevel);

    for (let i = 0; i < tempAreas.length; i++) {
      const extendedAreas: Area[] = this.extendAreasAroundArea(tempAreas[i], newAreas);
      newAreas = newAreas.concat(extendedAreas);
    }
    areas = newAreas.concat(areasOnLoadYLevel).concat(areasOnLoadHeightLevel).concat(otherAreas);
    if (load.pctSupport && !isRotated) {
      const newAreass: Area[] = createPCTFreeSpaces(load, loads, loadingSpace);
      const NUMBER_OF_MERGES = 9;
      let mergedAreas: Area[] = mergeAreas(newAreass);

      for (let i = 0; i < NUMBER_OF_MERGES; i++) {
        mergedAreas = mergeAreas(mergedAreas);
      }
      areas = this.removeCurrentLoadArea(areas, load);
      // areas = areas.filter((a: Area) => {
      //   return a.y === 0;
      // });
      areas = areas.concat(mergedAreas);
      areas = areas.concat(loadArea);
    }
    if (!load.pctSupport) {
      areas = this.removeContainedAreas(areas);
    }
    return [...areas, ...barrelAreas];
  }

  private removeCurrentLoadArea(areas: Area[], currentLoad: Load): Area[] {
    return areas.filter((a: Area) => {
      return !(
        a.x === currentLoad.x &&
        a.y === currentLoad.y + currentLoad.height &&
        a.z === currentLoad.z &&
        a.length === currentLoad.length &&
        a.width === currentLoad.width
      );
    });
  }

  public generateAreasStackingGroups(areas: Area[], groups: Group[]) {
    const groupsWithStacking: Group[] = groups.filter((g: Group) => g.stackingOptions);
    const areasWithStackingGroups: { id: number; areas: Area[] }[] = [];
    if (groupsWithStacking) {
      groupsWithStacking.forEach((g: Group): void => {
        let areasGroup: Area[] = [];
        g.loadsCubes.forEach((l: LoadsCube): void => {
          if (l.position) {
            const dimensions: Dimensions = l.dimensions ? l.dimensions : g.dimensions;
            const areaTmp: Area = new Area(
              dimensions.length * l.loadsPerAxis.z,
              dimensions.width * l.loadsPerAxis.x,
              dimensions.height * l.loadsPerAxis.y,
              l.position.x,
              l.position.y,
              l.position.z,
              AreaType.default,
            );
            areaTmp.times100();
            areasGroup = areasGroup.concat(
              areas.filter((area: Area) => area.isSamePositionAround(areaTmp)),
            );
          }
        });
        areasGroup = this.removeSameArea(areasGroup);
        areasWithStackingGroups.push({ id: g.id, areas: areasGroup });
      });
    }
    return areasWithStackingGroups;
  }

  private removeSameArea(areas: Area[]): Area[] {
    const uniqueArray: Area[] = areas.filter(
      (area: Area, index: number): boolean =>
        index === areas.findIndex((area1) => _.isEqual(area1, area)),
    );
    return uniqueArray;
  }

  private splitAreasAroundArea(area: Area, areas: Area[]): Area[] {
    let splitAreas = [];
    for (let i = 0; i < areas.length; i++) {
      if (_.isEqual(areas[i], area)) {
        areas.splice(i, 1);
        i--;
      } else {
        let tempSplitAreas = [];
        tempSplitAreas = tempSplitAreas.concat(this.splitAreasOnXAxis(areas[i], area));
        tempSplitAreas = tempSplitAreas.concat(this.splitAreasOnZAxis(areas[i], area));
        if (tempSplitAreas.length > 0) {
          areas.splice(i, 1);
          i--;
        }
        splitAreas = splitAreas.concat(tempSplitAreas);
      }
    }
    splitAreas = splitAreas.concat(areas);
    splitAreas = this.removeContainedAreas(splitAreas);
    return splitAreas as Area[];
  }

  private filterAreasAroundArea(area: Area, areas: Area[]): Area[] {
    return areas.filter(
      (a) =>
        area.isPartiallyExtendingBottomSide(a) ||
        area.isPartiallyExtendingLeftSide(a) ||
        area.isPartiallyExtendingRightSide(a) ||
        area.isPartiallyExtendingTopSide(a),
    );
  }

  private splitAreasOnXAxis(area: Area, area1: Area): Area[] {
    const splitAreas = [];
    if ((area.z <= area1.z && area.z2() > area1.z) || (area.z > area1.z && area.z < area1.z2())) {
      if (area1.x <= area.x && area1.x2() > area.x && area1.x2() < area.x2()) {
        splitAreas.push(
          new Area(
            area.length,
            area.x2() - area1.x2(),
            area.height,
            area1.x2(),
            area.y,
            area.z,
            area.type,
          ),
        );
      } else if (area1.x > area.x && area1.x2() < area.x2()) {
        splitAreas.push(
          new Area(area.length, area1.x - area.x, area.height, area.x, area.y, area.z, area.type),
        );
        splitAreas.push(
          new Area(
            area.length,
            area.x2() - area1.x2(),
            area.height,
            area1.x2(),
            area.y,
            area.z,
            area.type,
          ),
        );
      } else if (area1.x2() >= area.x2() && area1.x < area.x2() && area1.x > area.x) {
        splitAreas.push(
          new Area(area.length, area1.x - area.x, area.height, area.x, area.y, area.z, area.type),
        );
      }
    }
    return splitAreas as Area[];
  }

  private splitAreasOnZAxis(area: Area, area1: Area): Area[] {
    const splitAreas = [];
    if ((area.x <= area1.x && area.x2() > area1.x) || (area.x > area1.x && area.x < area1.x2())) {
      if (area1.z <= area.z && area1.z2() > area.z && area1.z2() < area.z2()) {
        splitAreas.push(
          new Area(
            area.z2() - area1.z2(),
            area.width,
            area.height,
            area.x,
            area.y,
            area1.z2(),
            area.type,
          ),
        );
      } else if (area1.z > area.z && area1.z2() < area.z2()) {
        splitAreas.push(
          new Area(area1.z - area.z, area.width, area.height, area.x, area.y, area.z, area.type),
        );
        splitAreas.push(
          new Area(
            area.z2() - area1.z2(),
            area.width,
            area.height,
            area.x,
            area.y,
            area1.z2(),
            area.type,
          ),
        );
      } else if (area1.z2() >= area.z2() && area1.z < area.z2() && area1.z > area.z) {
        splitAreas.push(
          new Area(area1.z - area.z, area.width, area.height, area.x, area.y, area.z, area.type),
        );
      }
    }
    return splitAreas as Area[];
  }

  private removeContainedAreas(areas: Area[]): Area[] {
    const bounds = areas.map((a: Area) => [a.x, a.x2(), a.y, a.y2(), a.z, a.z2()]);
    const alive = areas.map(() => true);
    for (let i = 0; i < areas.length; i++) {
      const [x, x2, y, y2, z, z2] = bounds[i];
      for (let j = 0; j < areas.length; j++) {
        if (i === j || !alive[j]) {
          continue;
        }
        const b = bounds[j];
        if (x >= b[0] && x2 <= b[1] && y >= b[2] && y2 <= b[3] && z >= b[4] && z2 <= b[5]) {
          alive[i] = false;
          break;
        }
      }
    }
    return areas.filter((_area: Area, i: number): boolean => alive[i]);
  }

  public extendAreasAroundArea(area: Area, areas: Area[]): Area[] {
    const newAreas: Area[] = [];
    const seen = new Set<string>();
    const add = (extended: Area): void => {
      const key = `${extended.x}|${extended.y}|${extended.z}|${extended.width}|${extended.height}|${extended.length}|${extended.type}`;
      if (!seen.has(key)) {
        seen.add(key);
        newAreas.push(extended);
      }
    };
    for (let i = 0; i < areas.length; i++) {
      if (area.isPartiallyExtendingBottomSide(areas[i])) {
        add(this.createAreaWhenAreaExtendsBottomSide(area, areas[i]));
      }
      if (area.isPartiallyExtendingTopSide(areas[i])) {
        add(this.createAreaWhenAreaExtendsTopSide(area, areas[i]));
      }
      if (area.isPartiallyExtendingLeftSide(areas[i])) {
        add(this.createAreaWhenAreaExtendsLeftSide(area, areas[i]));
      }
      if (area.isPartiallyExtendingRightSide(areas[i])) {
        add(this.createAreaWhenAreaExtendsRightSide(area, areas[i]));
      }
    }
    return newAreas;
  }

  private createAreaWhenAreaExtendsBottomSide(area: Area, area1: Area): Area {
    const { length, z } = this.calculateLengthAndZWhenExtendingLength(area, area1);
    return new Area(length, area.width + area1.width, area.height, area1.x, area.y, z, area.type);
  }

  private createAreaWhenAreaExtendsTopSide(area: Area, area1: Area): Area {
    const { length, z } = this.calculateLengthAndZWhenExtendingLength(area, area1);
    return new Area(length, area.width + area1.width, area.height, area.x, area.y, z, area.type);
  }

  private createAreaWhenAreaExtendsLeftSide(area: Area, area1: Area): Area {
    const { width, x } = this.calculateWidthAndXWhenExtendingWidth(area, area1);
    return new Area(area.length + area1.length, width, area.height, x, area.y, area1.z, area.type);
  }

  private createAreaWhenAreaExtendsRightSide(area: Area, area1: Area): Area {
    const { width, x } = this.calculateWidthAndXWhenExtendingWidth(area, area1);
    return new Area(area.length + area1.length, width, area.height, x, area.y, area.z, area.type);
  }

  private calculateWidthAndXWhenExtendingWidth(area: Area, area1: Area) {
    let width: number, x: number;
    if (area.x <= area1.x) {
      width = area.x2() >= area1.x2() ? area1.width : area.x2() - area1.x;
      x = area1.x;
    } else {
      width = area.x2() >= area1.x2() ? area1.x2() - area.x : area.width;
      x = area.x;
    }
    return { width, x };
  }

  private calculateLengthAndZWhenExtendingLength(area: Area, area1: Area) {
    let length: number, z: number;
    if (area.z <= area1.z) {
      length = area.z2() >= area1.z2() ? area1.length : area.z2() - area1.z;
      z = area1.z;
    } else {
      length = area.z2() >= area1.z2() ? area1.z2() - area.z : area.length;
      z = area.z;
    }
    return { length, z };
  }

  private getHeightOfSpaceThatHasLoadPosition(
    position: Vector3,
    loadingSpace: LoadingSpace,
    outside: Outside,
  ): number {
    let spaceHeight: number;
    if (loadingSpace.hasPosition(position)) {
      if (loadingSpace.isInFirstPart(position)) {
        spaceHeight = loadingSpace.height;
      } else {
        spaceHeight = loadingSpace.height1;
      }
    } else {
      spaceHeight = outside.dimensions.height;
    }
    return spaceHeight;
  }
}
